import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { INLINE_CTA } from "../../lib/blog-markers";

export const queMettreListeDeNaissance: BlogPost = {
  h1: "Que mettre sur une liste de naissance ? La liste complète, pièce par pièce",
  seoTitle: "Que mettre sur une liste de naissance : la liste complète et les oublis · Listou",
  seoDescription:
    "Sommeil, repas, toilette, sorties, éveil, vêtements : ce qu'il faut vraiment mettre sur sa liste de naissance, les tailles à prévoir, ce qu'on oublie toujours et ce qui ne sert à rien. Avec un exemple de liste par budget.",
  publishedOn: "2026-10-20",
  updatedOn: "2026-10-20",
  summary:
    "Une bonne liste de naissance couvre six rayons : le sommeil, les repas, la toilette et le change, les sorties, l'éveil et les vêtements. Comptez entre 25 et 40 envies, avec des prix étalés de 10 € à plusieurs centaines d'euros, pour que chaque proche trouve un cadeau à sa mesure. Les vêtements se demandent en 3, 6 et 12 mois plutôt qu'en naissance, et les gros achats, poussette ou lit, s'offrent à plusieurs.",
  body: `
## Combien d'envies mettre sur sa liste ?

Entre **25 et 40**, pour une famille et un cercle d'amis de taille moyenne. Moins, et les derniers à ouvrir la liste ne trouvent plus rien à réserver. Plus, et les proches s'y perdent.

Plus que le nombre, c'est l'étalement des prix qui compte. Une liste où tout coûte plus de 100 € met mal à l'aise la cousine étudiante ; une liste à 15 € maximum oblige les grands-parents à chercher ailleurs. Visez à peu près :

- **un tiers de petits prix**, sous 30 € : bodies, livres, bavoirs, hochets ;
- **un tiers de prix moyens**, de 30 à 100 € : gigoteuse, tapis d'éveil, baignoire, babyphone simple ;
- **quelques gros cadeaux**, au-delà de 100 € : poussette, lit, siège auto, porte-bébé physiologique.

Les gros cadeaux s'offrent souvent à plusieurs. Sur Listou, une envie peut porter une quantité : une poussette à 600 € notée en six parts de 100 € se réserve part par part.

## Le sommeil

C'est le rayon à soigner en premier : bébé dort seize heures par jour les premières semaines.

- **Un berceau, un couffin ou un cododo** pour les premiers mois, dans la chambre des parents.
- **Un lit à barreaux ou un lit évolutif**, pour la suite. Souvent le plus gros achat de la chambre.
- **Un matelas** ferme, aux dimensions exactes du lit, et **deux alèses** : une sur le lit, une au lavage.
- **Deux ou trois draps-housses.**
- **Deux gigoteuses** : une de naissance (0-6 mois), une pour la suite. On les demande dans des épaisseurs différentes si bébé naît entre deux saisons.
- **Une veilleuse** et, si la chambre est loin, **un babyphone**.

Pas de couette, d'oreiller ni de tour de lit : les recommandations de couchage les déconseillent pour les nourrissons. Les proches bien intentionnés les offrent quand même si la liste ne propose rien d'autre.

## Les repas

Ce rayon dépend de votre choix d'allaitement, et vous pouvez le remplir plus tard.

- **Allaitement** : un coussin d'allaitement, des coussinets, un tire-lait si vous reprenez le travail, des sachets de conservation.
- **Biberon** : quatre à six biberons, un goupillon, un chauffe-biberon ou un stérilisateur, une boîte doseuse pour le lait.
- **Dans tous les cas** : une dizaine de **langes** en coton, qui servent à tout, et des **bavoirs**.
- **Pour plus tard** : une chaise haute, un robot cuiseur, de la vaisselle et des cuillères souples. Les proches qui offrent après la naissance seront contents de les trouver.

## La toilette et le change

- **Une table à langer** ou un plan à langer posé sur une commode, avec **un matelas à langer** et deux housses.
- **Une baignoire** ou un transat de bain, et **un thermomètre de bain**.
- **Des capes de bain**, deux ou trois.
- **Une trousse de soin** : brosse douce, coupe-ongles, thermomètre, mouche-bébé.
- **Une poubelle à couches**, si la chambre est loin de la cuisine.

Les couches elles-mêmes se mettent rarement sur une liste : on ne sait pas encore quelle marque conviendra, et bébé change de taille vite. Si un proche veut en offrir, une carte cadeau d'une enseigne de puériculture fait mieux l'affaire.

## Les sorties

- **Une poussette**, avec nacelle ou siège coque pour les premiers mois. Le cadeau à plusieurs par excellence.
- **Un siège auto** groupe 0+ (ou i-Size 40-87 cm), obligatoire pour rentrer de la maternité en voiture.
- **Un porte-bébé** physiologique ou **une écharpe de portage**. Beaucoup de parents en utilisent un plus souvent que la poussette.
- **Un sac à langer.**
- **Une chancelière** ou une couverture de poussette si bébé naît en hiver, une ombrelle et un voile s'il naît en été.

## L'éveil

- **Un tapis d'éveil** avec arche, dès les premières semaines.
- **Un transat**, pour poser bébé près de soi.
- **Un mobile** au-dessus du lit.
- **Deux ou trois doudous**, dont un identique en secours : le jour où le doudou se perd, vous remercierez celui qui l'a offert.
- **Des hochets, des anneaux de dentition** et des **livres** en tissu ou en carton.

Ce rayon est celui que les proches remplissent d'eux-mêmes, même sans liste. Gardez-y des petits prix pour les enfants de la famille, qui aiment choisir un cadeau pour le bébé.
${INLINE_CTA}## Les vêtements : demandez les tailles qui suivent

Tout le monde offre du naissance, et bébé n'y tient que quelques semaines. Sur la liste, demandez plutôt :

- des **bodies** et des **pyjamas** en **3, 6 et 12 mois** ;
- des **gilets** et un **bonnet** ;
- une **combinaison pilote** à la taille de bébé l'hiver suivant.

Indiquez la saison dans la note de l'envie : un pyjama 6 mois pour un bébé né en avril se portera en octobre, il doit être chaud.

## Ce qu'on oublie toujours

- **Pour les parents** : un thermos, un repas livré, une séance de ménage, un livre pour les nuits blanches. La liste est aussi la leur.
- **Les souvenirs** : un livre de naissance, un kit d'empreintes, un cadre. Les parents ne pensent pas à se les offrir.
- **Le deuxième exemplaire** : deux gigoteuses, deux turbulettes, deux doudous, deux housses de matelas à langer. Un premier au lavage, l'autre en service.
- **Une participation** à un projet plus grand : la chambre, un week-end à trois, une épargne pour bébé. Sur Listou, vous ajoutez un lien vers votre cagnotte.

## Ce qui ne sert presque jamais

Le chauffe-lingettes, le stérilisateur si vous allaitez, les chaussons de naissance (bébé ne marche pas), le parc avant 6 mois, et les vêtements naissance en grande quantité. Rien n'empêche de les mettre sur la liste, mais pas avant le reste.

## Faire sa liste sans s'y perdre

Commencez par les rayons qui comptent le plus pour vous, et complétez au fil des semaines : une liste n'est jamais finie le jour où on la partage. Sur [Listou](/liste-de-naissance), on colle le lien d'un produit de n'importe quelle boutique et le nom, la photo et le prix sont récupérés. Chaque envie se marque **Coup de cœur**, **Envie** ou **Idée**, pour que les proches sachent par où commencer, et l'assistant suggère ce qu'on oublie souvent. Le co-parent ajoute ses idées de son côté, en co-propriétaire.

Pour trouver des idées rayon par rayon, notre guide des [idées cadeaux de naissance](/idees-cadeaux-naissance) recense les envies que les parents mettent le plus souvent sur leur liste. Et pour choisir où faire sa liste, voyez notre [comparatif des listes de naissance](/blog/meilleure-liste-de-naissance).
`,
  faq: [
    [
      "Combien d'articles mettre sur une liste de naissance ?",
      "Entre 25 et 40 pour une famille et un cercle d'amis de taille moyenne, avec des prix étalés : un tiers sous 30 €, un tiers entre 30 et 100 €, et quelques gros cadeaux à offrir à plusieurs.",
    ],
    [
      "Quels sont les indispensables d'une liste de naissance ?",
      "Un couchage (berceau ou lit, matelas, gigoteuses), un siège auto pour rentrer de la maternité, une poussette ou un porte-bébé, de quoi faire la toilette (baignoire, trousse de soin, table à langer) et des vêtements en 3, 6 et 12 mois.",
    ],
    [
      "Faut-il mettre des vêtements taille naissance sur sa liste ?",
      "Peu. Bébé n'y tient que quelques semaines et les proches en offrent spontanément. Demandez plutôt du 3, 6 et 12 mois, en précisant la saison dans la note.",
    ],
    [
      "Peut-on mettre une poussette sur une liste de naissance ?",
      "Oui, c'est même le cadeau à plusieurs le plus courant. Sur Listou, on lui donne une quantité, par exemple six parts de 100 €, et chaque proche réserve sa part.",
    ],
    [
      "Peut-on demander de l'argent sur une liste de naissance ?",
      "Oui, pour un projet plus grand (la chambre, une épargne pour bébé) ou en complément des cadeaux. Listou ne fait pas transiter d'argent, mais vous ajoutez un lien vers votre cagnotte.",
    ],
    [
      "Que ne faut-il pas mettre sur une liste de naissance ?",
      "Couette, oreiller et tour de lit, déconseillés pour le couchage des nourrissons. Les couches, dont la marque et la taille changent vite. Et en petite quantité seulement : chaussons, vêtements naissance, chauffe-lingettes.",
    ],
  ],
  occasion: "birth",
  inlineCta: {
    title: "Notez ces envies au fur et à mesure",
    text: "Collez le lien d'un produit de n'importe quelle boutique : nom, photo et prix sont récupérés.",
    sticker: stickerUrl("bebe", "biberon"),
  },
};
